import React from 'react'
import { Box, Typography, alpha } from '@mui/material'
import { Block, Check } from '@mui/icons-material'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'

type LocationsSummaryProps = {
  total: number
  locationType: LocationTypesEnum
  /** Number of larger areas covered (the régions of the targeted départements), when known */
  groupCount?: number
  /** The locations are excluded rather than targeted: tinted header, "excluded" */
  exclusive?: boolean
  /** The adgroup market, named in the exclusion hint */
  countryCode?: string
}

const LocationsSummary: React.FC<LocationsSummaryProps> = ({
  total,
  locationType,
  groupCount,
  exclusive = false,
  countryCode,
}) => {
  const t = useAppTranslations()
  const RuleIcon = exclusive ? Block : Check

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'wrap',
        minHeight: 40,
        py: 0.75,
        px: 1.5,
        boxSizing: 'border-box',
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: exclusive ? alpha(theme.palette.error.main, 0.06) : undefined,
      })}
    >
      <RuleIcon fontSize="small" color={exclusive ? 'error' : 'primary'} aria-hidden />
      <Typography component="span" sx={{ fontWeight: 700 }}>
        {total}
      </Typography>
      <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
        {t(`adgroup.geoTargeting.${locationType}`)}
      </Typography>
      <Typography
        component="span"
        variant="body2"
        sx={{ fontWeight: 500, color: exclusive ? 'error.main' : 'primary.main' }}
      >
        {t(exclusive ? 'adgroup.geoTargeting.panel.excluded' : 'adgroup.geoTargeting.panel.targeted')}
      </Typography>
      {groupCount !== undefined && groupCount > 0 && (
        <Typography component="span" variant="body2" color="textSecondary">
          {t('adgroup.geoTargeting.panel.regionCount', { count: groupCount })}
        </Typography>
      )}
      <Typography component="span" variant="body2" color="textSecondary">
        ·{' '}
        {exclusive
          ? t('adgroup.geoTargeting.ruleHintExclude', { countryCode: countryCode?.toUpperCase() })
          : t('adgroup.geoTargeting.ruleHintInclude')}
      </Typography>
    </Box>
  )
}

export default LocationsSummary
